import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
	TEXT,
	NUMBER,
	RADIO,
	TOGGLE_JSON_TREE,
	SAVE_CURRENT_QUESTION_TREE_ID,
} from '#app/constants/forms.ts'
import { getCurrentQuestionFromJSONState } from '#app/features/form/wizard/utils/getFromJSONTree.ts'
import { type DynamicQuestionComponentProps } from '#types/form/wizard.ts'
import { RadioButtonComponent } from './radioFieldComponent.tsx'
import { TextFieldComponent } from './textFieldComponent.tsx'

export const DynamicQuestionComponent = ({
	fieldKey = '',
	control,
	currentQuestionTreeId,
	canBeParent,
	editableParents,
	questionTree,
	answeredQuestionsStack,
	register,
	dispatch,
	getValues,
	handleSubmit,
	watch,
	isController = false,
}: DynamicQuestionComponentProps) => {
	const { t } = useTranslation()

	const currentQuestionStack = answeredQuestionsStack.find(
		({ id }) => id === currentQuestionTreeId,
	)

	const currentQuestionId = currentQuestionStack?.currentQuestionId ?? 'A1'

	const currentQuestion = getCurrentQuestionFromJSONState({
		questionTree: questionTree!,
		currentQuestionId: currentQuestionId as string,
		currentQuestionTreeId,
	})

	useEffect(() => {
		dispatch({
			type: SAVE_CURRENT_QUESTION_TREE_ID,
			currentQuestionId,
			currentQuestionTreeId,
		})

		dispatch({
			type: TOGGLE_JSON_TREE,
			isInJsonTree: true,
		})
	}, [currentQuestionId, currentQuestionTreeId, dispatch])

	return (
		<>
			{currentQuestion?.type === RADIO && (
				<RadioButtonComponent
					key={fieldKey}
					id={fieldKey}
					isController={isController}
					watch={watch}
					getValues={getValues}
					dispatch={dispatch}
					handleSubmit={handleSubmit}
					control={control}
					values={currentQuestion.options.map(option => ({
						id: option.id,
						title: option.text,
						includedQuestions: option?.includedQuestions,
					}))}
					fieldName={fieldKey}
					label={t(currentQuestion.text)}
				/>
			)}
			{currentQuestion?.type === TEXT && (
				<>
					<TextFieldComponent
						canBeParent={canBeParent}
						editableParents={editableParents}
						isParentProps={{
							id: currentQuestionTreeId,
							questionTree: questionTree!,
						}}
						watch={watch}
						getValues={getValues}
						register={register}
						dispatch={dispatch}
						type="textArea"
						fieldName={fieldKey}
						label={t(currentQuestion.text)}
					/>
				</>
			)}
			{currentQuestion?.type === NUMBER && (
				<>
					<TextFieldComponent
						canBeParent={canBeParent}
						editableParents={editableParents}
						isParentProps={{
							id: currentQuestionTreeId,
							questionTree: questionTree!,
						}}
						watch={watch}
						getValues={getValues}
						register={register}
						dispatch={dispatch}
						type="number"
						fieldName={fieldKey}
						label={t(currentQuestion.text)}
					/>
				</>
			)}
			{currentQuestion?.type === undefined && (
				<p className="-mt-3 mb-3 text-[1rem] font-bold text-error-300">
					Something went wrong, please refresh the page
				</p>
			)}
		</>
	)
}
